<template>
  <div
    v-tooltip.hover.top="tooltipData"
    class="equipment-panel"
    :class="severityClass"
  >
    <header class="body-4">
      <icon
        v-if="errors.length > 0"
        :icon="['fas', 'exclamation-triangle']"
        class="mr-2"
      />
      <slot name="header"></slot>
    </header>

    <button
      :class="{ disabled: disableToggle }"
      :disable="disableToggle"
      class="panel panel-trigger heading-3"
      @click="toggle"
    >
      <slot name="trigger">
        <template v-if="!isTriggerSlotEmpty">
          <span id="trigger-slot">
            {{ triggerSlotLabelFormatted }}
          </span>
        </template>
      </slot>
    </button>
  </div>
</template>

<script setup lang="ts">
import { useEditPermissions } from "@/composables/auth/editPermissions";
import { scenarioValidatorKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import type { ValidationTestName } from "@/composables/validation/validationTestName";
// import type { UseScenarioValidator } from "@/composables/validation/scenarioValidator";
// import { useStackValidator } from "@/composables/scenarioDetailProvider/useStackValidator";
// import { useHasDualStacks } from "@/composables/scenarioDetailProvider/stackFiltering";
import { injectStrict } from "@/utils/utils";
import type { PropType } from "vue";
import { computed, ref, useSlots } from "vue";

const props = defineProps({
  disableToggle: {
    type: Boolean,
    default: false
  },
  xrefId: {
    type: String,
    default: undefined
  },
  ruleNames: {
    type: Array as PropType<ValidationTestName[]>,
    default: () => []
  },
  triggerLabelName: {
    type: String,
    default: "Option"
  },
  stack: {
    type: String as PropType<"upper" | "lower" | undefined>,
    default: undefined
  }
});

const emit = defineEmits<{
  (event: "toggle"): void;
}>();

const slots = useSlots();

const isTriggerSlotEmpty = computed(() => {
  return slots.trigger && slots.trigger.length > 0;
});

const triggerSlotLabelFormatted = computed(
  () => `Please Select ${props.triggerLabelName}`
);

const { isScenarioReadonly, isImportedFromLegacy } = useEditPermissions();

const isOpen = ref(false);
const toggle = () => {
  if (
    isScenarioReadonly.value ||
    isImportedFromLegacy.value ||
    props.disableToggle
  )
    return;

  isOpen.value = !isOpen.value;
  emit("toggle");
};

// const equipment = injectStrict(equipmentKey);
// const hasDualStacks = useHasDualStacks(equipment);
const { filterByXrefIdAndRuleNames } = injectStrict(scenarioValidatorKey);
const errors = filterByXrefIdAndRuleNames(props.ruleNames, props.xrefId);

// // Use stack-aware validator when dual stacks are enabled and stack prop is provided
// const stackValidator = computed(() => {
//   if (
//     hasDualStacks.value &&
//     props.stack &&
//     (props.stack === "upper" || props.stack === "lower")
//   ) {
//     return useStackValidator(props.stack) as UseScenarioValidator;
//   }
//   return null;
// });

// // Use legacy validator when dual stacks are not enabled or stack prop is not provided
// const legacyValidator = computed(() => {
//   if (!hasDualStacks.value || !props.stack) {
//     return injectStrict(scenarioValidatorKey);
//   }
//   return null;
// });

// // Get the appropriate validator - guaranteed to have a value
// const validator = computed(() => {
//   return stackValidator.value || legacyValidator.value;
// });

// const errors = computed(() => {
//   const v = validator.value;
//   if (!v) {
//     return [];
//   }
//   return v.filterByXrefIdAndRuleNames(props.ruleNames, props.xrefId).value;
// });

const severityLevels = ["success", "info", "warn", "error", "catastrophe"];

// const severityClass = computed(() => {
//   if (errors.value.length === 0) {
//     return severityLevels[0]; // success
//   }
//   return severityLevels[Math.max(...errors.value.map(err => err.severity))];
// });
const severityClass = computed(
  () => severityLevels[Math.max(...errors.value.map(err => err.severity))]
);
const tooltipData = computed(() => ({
  value: errors.value[0]?.message + (errors.value.length > 1 ? `...` : ""),
  disabled: !errors.value[0],
  class: severityClass.value
}));
</script>

<style lang="scss" scoped>
.equipment-panel {
  $border: 1px solid var(--border-main);
  color: var(--gray-700);
  display: flex;
  flex-direction: column;
  border-radius: $borderRadius;
  background: var(--surface-25);
  border: $border;
  position: relative;
  box-shadow: var(--shadow-main);
  overflow: hidden;

  &.warn {
    border: 1px solid var(--warning-500);
    color: var(--warning-500);
  }
  &.error,
  &.catastrophe {
    border: 1px solid var(--danger-500);
    color: var(--danger-500);
  }

  header {
    position: absolute;
    top: 0.5em;
    left: 0.5em;
  }

  .panel {
    display: inline-block;
    padding: 0.5em;
    padding-top: 1.75em;
    transition: all 0.25s ease-in-out;
    border-radius: $borderRadius;
    color: var(--gray-900);
    font-weight: 600;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .panel-trigger {
    cursor: pointer;
    border-width: unset;
    border-style: unset;
    border-color: unset;
    border-image: unset;
    text-align: unset;
    align-items: unset;
    background-color: unset;

    #trigger-slot {
      font-style: italic;
      font-weight: 300;
    }

    &.disabled {
      cursor: unset;
      background: var(--surface-300);
      color: var(--gray-800);
      &:hover {
        background: var(--surface-300);
      }
    }

    &:hover {
      background: var(--surface-hover);
    }
  }
}
</style>
